// src/components/SubmitTime/fields/NumericDecimalField.tsx
// Modern React implementation of NumericDecimalField custom field

import React, { useState, useEffect } from 'react';
import type { CustomFieldProps } from '../types';
import './NumericDecimalField.css';

interface NumericDecimalFieldProps extends CustomFieldProps {}

export const NumericDecimalField: React.FC<NumericDecimalFieldProps> = ({
  customField,
  canEdit,
  handleDynamicInputChange,
  UpdateCustomFields,
  showRequiredError,
}) =>
{
  const [value, setValue] = useState<string | number>(
    customField.Value === null || customField.Value === undefined ? '' : customField.Value,
  );

  // Update value when customField prop changes
  useEffect(() =>
  {
    const newValue =
      customField.Value === null || customField.Value === undefined ? '' : customField.Value;
    setValue(newValue);
  }, [customField.Value]);

  const isDecimal = (x: string): string =>
  {
    // Allow numbers, one decimal point, and optional minus sign at start
    const regex = /^-?\d*\.?\d*$/;
    if (regex.test(x))
    {
      return x;
    }
    return value.toString();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const inputValue = event.target.value;
    const parsedValue = isDecimal(inputValue);
    setValue(parsedValue);
    handleDynamicInputChange(
      customField.CustomFieldId,
      parsedValue === '' ? null : Number(parsedValue),
    );
  };

  const handleBlur = () =>
  {
    // Format the value on blur if it's a valid number
    if (value !== '' && value !== '-')
    {
      const numValue = Number(value);
      if (!isNaN(numValue))
      {
        // Round to 2 decimal places
        const formatted = Math.round(numValue * 100) / 100;
        setValue(formatted);
        handleDynamicInputChange(customField.CustomFieldId, formatted);
      }
    }

    if (UpdateCustomFields)
    {
      UpdateCustomFields(customField);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) =>
  {
    // Allow: backspace, delete, tab, escape, enter, arrows, decimal point, minus sign
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      '.',
      '-',
    ];

    if (
      allowedKeys.includes(event.key) ||
      (event.key >= '0' && event.key <= '9') ||
      (event.ctrlKey &&
        (event.key === 'a' || event.key === 'c' || event.key === 'v' || event.key === 'x'))
    )
    {
      return;
    }

    event.preventDefault();
  };

  const isFieldDisabled = !canEdit || !customField.ConversationIsEnable;

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`corgee-inpuSubmitTimet right numeric-decimal-field${showRequiredError ? ' submit-required-error' : ''}`}
      disabled={isFieldDisabled}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={customField.Placeholder}
      autoComplete="off"
      step="0.01"
    />
  );
};

export default NumericDecimalField;
